import FormSelect from 'components/Select/FormSelect';
import Text from 'components/Text/Text';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { IItemForGetShortComboItemInfo } from 'types';
import { Checkbox } from 'antd';
import SearchField from 'components/SearchField/SearchField';
import ChunkOfTextWithCopying from 'components/Text/ChunkOfTextWithCopying';
import style from './MultipleSelect.module.scss';

interface IMultipleSelect {
	data: IItemForGetShortComboItemInfo[];
	isLoading: boolean;
	refetch: () => void;
	responseError: boolean;
	setComboItems: (value: string[]) => void;
	selectedComboItems: string[];
	selectedRestaurants: string[];
}

const MultipleSelect: React.FC<IMultipleSelect> = ({
	data,
	isLoading,
	refetch,
	responseError,
	selectedRestaurants,
	setComboItems,
	selectedComboItems,
}) => {
	const [searchString, setSearchString] = useState<string>('');

	const filteredDataWithAdditionalContent = useMemo(() => {
		const updatedData = data.map((el) => ({
			...el,
			additionalContent: (
				<>
					{el.dishExternalId && (
						<ChunkOfTextWithCopying text={el.dishExternalId} className={style.externalId} />
					)}
				</>
			),
		}));

		if (searchString) {
			const filteredDataByName = updatedData.filter((el) =>
				el.name.toLowerCase().includes(searchString.toLowerCase()),
			);
			const filteredDataByExternalId = updatedData.filter((el) =>
				el.dishExternalId?.toLowerCase().includes(searchString.toLowerCase()),
			);
			const unionFilteredData = Array.from(new Set([...filteredDataByName, ...filteredDataByExternalId]));
			return unionFilteredData;
		}
		return updatedData;
	}, [searchString, data]);

	const handleSelectAllData = useCallback(() => {
		const isCheckedAllItems = data.every((el) => selectedComboItems.includes(el.id));
		const dataIDs = data.map((el) => el.id);
		if (isCheckedAllItems) {
			setComboItems(selectedComboItems.filter((el) => !dataIDs.includes(el)));
		} else {
			const filteredData = data.map((el) => el.id).filter((el) => !selectedComboItems.includes(el));
			setComboItems([...selectedComboItems, ...filteredData]);
		}
	}, [data, selectedComboItems, setComboItems]);

	useEffect(() => {
		setSearchString('');
	}, [selectedRestaurants]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => {
			const isChecked = () => {
				return data.every((el) => selectedComboItems.includes(el.id));
			};

			return (
				<>
					<div className={style.selectMenuHeader}>
						<Checkbox
							className={style.checkBox}
							checked={!!(data.length && isChecked())}
							disabled={!data.length}
							onChange={handleSelectAllData}>
							<Text
								translationNameSpace="form"
								translationKey="selectAllComboItemsLabel"
								asFragment={true}
							/>
						</Checkbox>
						<SearchField
							onChangeSelectSearchString={setSearchString}
							searchString={searchString}
							className={style.searchField}
						/>
					</div>
					{menu}
				</>
			);
		},
		[selectedComboItems, data, handleSelectAllData, setSearchString, searchString],
	);

	return (
		<FormSelect
			name="selectedComboItems"
			label="comboItemLabel"
			options={filteredDataWithAdditionalContent}
			fetchError={responseError}
			loading={isLoading}
			onRefetch={refetch}
			disabled={!selectedRestaurants.length || isLoading}
			onChange={setComboItems}
			counterValue={selectedComboItems.length}
			dropdownRender={renderSelectMenu}
			mode="multiple"
		/>
	);
};

export default React.memo(MultipleSelect);
